import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { PRODUCTS } from "../data/content";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";

const ProductPage = ({ productId }) => {
  const location = useLocation();
  const [product, setProduct] = useState(null);

  useEffect(() => {
    const currentProduct = PRODUCTS.find((p) => p.id === productId);
    setProduct(currentProduct);
  }, [productId, location.pathname]);

  // Fallback handling if product or gallery is empty/undefined
  if (!product || !product.gallery || product.gallery.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <p className="eyebrow text-forest">Loading Product Content...</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${product.title} — ${product.tagline} | Rudrash Private Limited`}
        description={product.description}
      />
      {/* Key prop ensures the component and its animations completely reset tightly on route change */}
      <div key={location.key || location.pathname} data-testid={`page-${product.id}`}>
        <PageHero
          eyebrow={`Vertical — ${product.title}`}
          title={product.tagline}
          subtitle={product.description}
          image={product.heroImage}
          cta={{ label: "Request a sample", to: "/contact" }}
        />

        <section className="py-20 md:py-28 bg-cream">
          <div className="container mx-auto px-6 md:px-10 xl:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <FadeUp className="lg:col-span-5">
              <p className="eyebrow text-forest">Key Highlights</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest leading-tight">
                Engineered for quality, at every step.
              </h2>
            </FadeUp>
            <Stagger className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {product.highlights.map((h, i) => (
                <StaggerItem key={h}>
                  <div
                    data-testid={`highlight-${i}`}
                    className="flex items-start gap-4 p-6 border border-border-soft bg-cream-2/40 hover:bg-cream-2 transition-colors"
                  >
                    <CheckCircle2 size={22} className="text-saffron shrink-0 mt-1" />
                    <div>
                      <p className="eyebrow text-muted-ink">0{i + 1}</p>
                      <p className="mt-2 font-serif text-xl text-forest">{h}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-cream-2">
          <div className="container mx-auto px-6 md:px-10 xl:px-16">
            <FadeUp className="mb-12 max-w-2xl">
              <p className="eyebrow text-forest">Inside the Cultivation</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest">
                A look at our {product.title.toLowerCase()} operations.
              </h2>
            </FadeUp>
            <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {product.gallery.map((g, i) => (
                <StaggerItem
                  key={`${product.id}-gallery-${i}`}
                  className={`${i % 3 === 0 ? "row-span-2 md:row-span-2" : ""}`}
                >
                  <div
                    data-testid={`gallery-${i}`}
                    className={`overflow-hidden ${i % 3 === 0 ? "aspect-[3/4]" : "aspect-square"}`}
                  >
                    <img
                      src={g}
                      alt={`${product.title} ${i + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]"
                      loading="lazy"
                    />
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section className="py-24 bg-forest text-cream">
          <div className="container mx-auto px-6 md:px-10 xl:px-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <p className="eyebrow text-saffron">Ready to order?</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                Partner with Rudrash for premium {product.title.toLowerCase()} supply.
              </h2>
            </div>
            <Link to="/contact" className="btn-accent" data-testid="product-cta">
              Get in touch <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductPage;
